import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

export default function CourseMarketplace() {
  const navigate = useNavigate();
  const handleSignupOption = (role: "teacher" | "student" | "organization") => {
    navigate(`/signup/${role}`);
  };
  return (
    <main className="relative overflow-hidden py-20 sm:py-28">
      {/* Modern gradient background */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }}
        className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-violet-50"
        aria-hidden="true">
        <div className="absolute inset-0 opacity-30">
          <svg
            className="h-full w-full"
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%">
            <defs>
              <pattern
                id="dotPattern"
                width="30"
                height="30"
                patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="rgba(99, 102, 241, 0.4)" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dotPattern)" />
          </svg>
        </div>

        {/* Abstract shapes */}
        <div className="absolute top-0 right-0 -mt-16 -mr-16 h-96 w-96 rounded-full bg-indigo-100 opacity-20 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 h-96 w-96 rounded-full bg-violet-200 opacity-20 blur-3xl"></div>
      </motion.div>

      {/* Content Container */}
      <motion.div
        whileHover={{ y: -8 }}
        transition={{ type: "spring", stiffness: 300 }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }} className="text-base font-semibold text-indigo-600 tracking-wide uppercase">
            Course Marketplace
          </motion.h2>
          <motion.h1
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }} className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl lg:text-5xl leading-tight">
            Learn from expert teachers at your own pace
          </motion.h1>
          <div className="mx-auto mt-4 h-1 w-20 bg-indigo-500 rounded" />
          <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }} className="mt-6 text-xl text-gray-500 leading-relaxed">
            Our platform connects students with educators offering high-quality
            courses across various subjects. Pay only for the courses you want
            to take, with no subscription or hidden fees.
          </motion.p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-5 gap-8 items-center">
          {/* Left Side - Benefits */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-indigo-50">
              <motion.h3
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }} className="text-2xl font-bold text-gray-900 mb-6">
                How It Works
              </motion.h3>

              <ul className="space-y-5">
                {[
                  {
                    title: "Find your perfect course",
                    description:
                      "Browse through our catalog of courses taught by verified expert educators",
                  },
                  {
                    title: "Pay once, learn forever",
                    description:
                      "Purchase courses individually with lifetime access to all materials",
                  },
                  {
                    title: "Learn at your own pace",
                    description:
                      "Access course content anytime, anywhere on any device",
                  },
                  {
                    title: "Engage with teachers",
                    description:
                      "Get direct feedback and support from your course instructors",
                  },
                ].map((item, index) => (
                  <motion.li
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }} key={index} className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-md bg-indigo-600 text-white">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-medium text-gray-900">
                        {item.title}
                      </h4>
                      <p className="mt-1 text-gray-500">{item.description}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>

          {/* Middle - Illustration */}
          <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }} className="hidden lg:block lg:col-span-1">
            <div className="h-full flex items-center justify-center">
              <div className="relative h-56 w-px bg-gradient-to-b from-transparent via-indigo-400 to-transparent">
                <div className="absolute top-1/4 -left-3 h-6 w-6 rounded-full bg-indigo-100 border-2 border-indigo-400"></div>
                <div className="absolute top-2/4 -left-3 h-6 w-6 rounded-full bg-indigo-200 border-2 border-indigo-500"></div>
                <div className="absolute top-3/4 -left-3 h-6 w-6 rounded-full bg-indigo-300 border-2 border-indigo-600"></div>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Featured Courses Preview */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-indigo-50">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Featured Categories
              </h3>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: "Mathematics", count: 45 },
                  { name: "Science", count: 38 },
                  { name: "Language Arts", count: 29 },
                  { name: "History", count: 22 },
                  { name: "Computer Science", count: 31 },
                  { name: "Art & Music", count: 19 },
                  { name: "Backend Development", count: 19 },
                  { name: "Frontend Development", count: 19 },
                  { name: "Others", count: 19 },
                ].map((category, index) => (
                  <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }}
                    key={index}
                    className="bg-gradient-to-br from-white to-indigo-50 p-4 rounded-lg border border-indigo-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                    <p className="font-medium text-gray-900">{category.name}</p>
                    {/* <p className="text-sm text-indigo-600">
                      {category.count} courses
                    </p> */}
                  </motion.div>
                ))}
              </div>

               <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }} className="mt-8">
                <a
                  href="/courses"
                  className="w-full inline-flex items-center justify-center px-6 py-3 
                  border border-transparent text-base font-medium rounded-lg 
                  text-white bg-indigo-600 hover:bg-indigo-700 
                  transition-all duration-200 ease-in-out 
                  shadow-md hover:shadow-lg">
                  Explore All Courses
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 ml-2"
                    viewBox="0 0 20 20"
                    fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Teacher CTA */}
        <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }} className="mt-16 lg:mt-24 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl shadow-xl overflow-hidden">
          <div className="px-6 py-12 sm:px-12 lg:flex lg:items-center lg:py-16">
            <div className="lg:w-0 lg:flex-1">
              <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                Are you an educator?
              </h2>
              <p className="mt-4 max-w-3xl text-lg text-indigo-100">
                Join our platform to create and sell your courses to students
                worldwide. You set the price, we handle the technology.
              </p>
            </div>
            <div className="mt-8 lg:mt-0 lg:ml-8">
              <div className="sm:flex">
                <a
                  onClick={() => handleSignupOption("teacher")}
                  className="flex cursor-pointer items-center justify-center px-6 py-3 
                  border border-transparent text-base font-medium rounded-lg 
                  text-indigo-700 bg-white hover:bg-indigo-50 
                  transition-all duration-200 ease-in-out 
                  shadow-lg hover:shadow-xl">
                  Start Teaching Today
                </a>
              </div>
              <p className="mt-3 text-sm text-indigo-100">
                No technical skills required. Get started in minutes.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </main>
  );
}
